import React, { useState, useImperativeHandle, useRef, useEffect } from "react";
import styled from "styled-components";

import { Svg } from "./Svg";
import { mdiChevronDown } from "@mdi/js";

const SelectStyle = styled.div`
    position: relative;
`;

const ContainerStyle = styled.div`
    height: 26px;
    display: flex;
    border-radius: 2px;
    box-sizing: border-box;
    background-color: ${props => props.theme.white};
    border: 1px solid ${props => props.theme.greyLight};
    &:focus-within {
        border-color: ${props => props.theme.white};
        outline: 1px solid ${props => props.theme.primaryLight};
    }
    &.disabled {
        pointer-events: none;
        background-color: ${props => props.theme.greyLighter};
    }
    &.error {
        background-color: ${props => props.theme.redLight};
        border-color: ${props => props.theme.redDark};
        &:focus-within {
            border-color: ${props => props.theme.redLight};
            outline: 1px solid ${props => props.theme.redDarker};
        }
    }
`;

const TitleStyle = styled.p`
    height: 12px;
    font-size: 12px;
    margin-bottom: 1px;
    letter-spacing: 0.4px;
    color: ${props => props.theme.black};
`;

const InputStyle = styled.input`
    cursor: ${props => (props.$editable === true ? "text" : "pointer")};
    cursor: pointer;
    width: 100%;
    border: none;
    display: block;
    font-weight: 400;
    box-sizing: border-box;
    padding: 3px 5px 2px 10px;
    background-color: transparent;
    color: ${props => props.theme.black};
    text-align: ${props => props.$textAlign};
    &::placeholder {
        color: ${props => props.theme.greyLight};
    }
`;

const SymbolStyle = styled.div`
    cursor: pointer;
    height: 25px;
    display: flex;
    padding: 0 1px;
    align-items: center;
    box-sizing: border-box;
    border-left: 1px solid ${props => props.theme.greyLight};
    &.error {
        border-color: ${props => props.theme.redDark};
    }
`;

const OptionsStyle = styled.div`
    z-index: 10;
    display: block;
    font-size: 14px;
    position: absolute;
    margin-top: 5px;
    padding-top: 4px;
    padding-bottom: 4px;
    overflow-y: auto;
    max-height: 200px;
    overflow-x: visible;
    border-radius: 2px;
    background: ${props => props.theme.white};
    outline: 1px solid ${props => props.theme.primaryLight};
    &.error {
        outline: 1px solid ${props => props.theme.redDarker};
    }
`;

const OptionStyle = styled.div`
    cursor: pointer;
    display: block;
    user-select: none;
    padding: 7px 30px 7px 10px;
    &:hover {
        background: ${props => props.theme.greyLightest};
    }
`;

const ErrorStyle = styled.p`
    height: 0;
    opacity: 0;
    font-size: 11px;
    visibility: hidden;
    color: ${props => props.theme.redDark};
    transition: opacity 0.3s, height 0.3s, visibility 0.3s;
    &.error {
        opacity: 1;
        height: 11px;
        margin-top: 2px;
        visibility: visible;
    }
`;

export const SelectSingle = props => {

    const { id, ref, name, title, value, maxLength, disabled, editable, textAlign = "left", children, error, errorVisible } = props;

    const inputRef = useRef(null);

    const errorName = name ? name + "ErrorText" : "errorText";

    const disabledClassName = disabled ? "disabled" : "";
    const errorClassName = (errorVisible && error) ? "error" : "";
    const className = [disabledClassName, errorClassName].join(" ");

    const [options, setOptions] = useState([]);
    const [showOptions, setShowOptions] = useState(false);
    const [openOptions, setOpenOptions] = useState(false);

    useImperativeHandle(ref, () => ({
        get value() {
            return value;
        },
        focus: () => inputRef.current.focus(),
        addEventListener: (type, listener) => inputRef.current.addEventListener(type, listener),
        removeEventListener: (type, listener) => inputRef.current.removeEventListener(type, listener)
    }), [inputRef, value]);

    useEffect(() => {
        (async () => {
            if (showOptions) {
                const options = await buildOptions(children);
                setOptions(options);
                setOpenOptions(options.length > 0);
            } else {
                setOpenOptions(false);
            }
        })();
    }, [showOptions, value]);

    const onChange = event => {
        const newValue = event.target.value;
        setShowOptions(true);
        props.onChange(newValue);
    };

    //prevents the select from losing focus when option is clicked
    const onMouseDown = event => event.preventDefault();

    const onBlur = () => setShowOptions(false);

    const onClick = () => {
        inputRef.current.focus();
        setShowOptions(!showOptions);
    };

    const toLabel = () => {
        const label = props.toLabel ? props.toLabel(value) : value;
        return (label === null || label === undefined) ? "" : label;
    };

    const onKeyDown = event => {
        if (props.onKeyDown) {
            props.onKeyDown(event);
        }
    };

    const onClickOption = element => {
        const clickedValue = element.props.value;
        if (value !== clickedValue) {
            props.onChange(clickedValue);
        }
        setShowOptions(false);
    };

    const buildOptions = async childs => {
        let childrenArray = [];
        if (typeof childs === "function") {
            childrenArray = await Promise.resolve(childs());
        } else {
            childrenArray = React.Children.toArray(childs);
        }
        if (value !== null && editable) {
            childrenArray = childrenArray.filter(child => child.props.value.toString().toLowerCase().includes(value.toLowerCase()));
        }
        return childrenArray.map((child, index) =>
            <OptionStyle
                key={child.key || index}
                value={child.props.value}
                onMouseDown={onMouseDown}
                onClick={() => onClickOption(child)}>
                {child.props.children}
            </OptionStyle>
        );
    };

    return (
        <SelectStyle>
            {title !== false &&
                <TitleStyle>{title}</TitleStyle>
            }
            <ContainerStyle className={className}>
                <InputStyle
                    id={id}
                    name={name}
                    type="text"
                    ref={inputRef}
                    onBlur={onBlur}
                    onClick={onClick}
                    onChange={onChange}
                    value={toLabel()}
                    readOnly={!editable}
                    disabled={disabled}
                    maxLength={maxLength}
                    onKeyDown={onKeyDown}
                    className={errorClassName}
                    $textAlign={textAlign}
                    $editable={editable} />
                <SymbolStyle className={errorClassName} onMouseDown={onMouseDown} onClick={onClick}>
                    <Svg path={mdiChevronDown} size="20" />
                </SymbolStyle>
            </ContainerStyle>
            {openOptions &&
                <OptionsStyle
                    className={errorClassName}>
                    {options.map(option => option)}
                </OptionsStyle>
            }
            <ErrorStyle
                name={errorName}
                className={typeof error === "string" ? errorClassName : ""}>
                {error}
            </ErrorStyle>
        </SelectStyle>
    );
};